import { ApiClient } from './client';
import { DEFAULT_THREAD_ID } from '../utils/constants';
import type { ChatResponse, ModelName, Message, FileAttachment } from '../types';

export class ChatApiService {
  private client: ApiClient;

  constructor(client?: ApiClient) {
    this.client = client || new ApiClient();
  }

  async checkHealth(): Promise<{ status: string; timestamp: string }> {
    return this.client.healthCheck();
  }

  async sendMessage(
    message: string,
    modelName: ModelName,
    threadId: string = DEFAULT_THREAD_ID,
    attachments?: FileAttachment[],
    signal?: AbortSignal,
    _isFirstMessage: boolean = false,
    flag: boolean = false
  ): Promise<{ response: string; attachments?: FileAttachment[] }> {
    if (!message?.trim()) {
      throw new Error('Message cannot be empty');
    }

    if (!modelName) {
      throw new Error('Model name is required');
    }

    try {
      let apiResponse: ChatResponse;
      
      // Если есть файл, используем multipart/form-data
      if (attachments && attachments.length > 0) {
        const firstFile = attachments[0];
        
        if (firstFile.data && firstFile.data.trim()) {
          const base64Data = firstFile.data;
          const base64Match = base64Data.match(/^data:([^;]+);base64,(.+)$/);
          const mimeType = base64Match ? base64Match[1] : (firstFile.type || 'application/octet-stream');
          const base64String = base64Match ? base64Match[2] : base64Data;
          
          const byteCharacters = atob(base64String);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });
          
          const file = new File([blob], firstFile.name || 'file', { type: mimeType });
          
          const formData = new FormData();
          formData.append('message', message.trim());
          formData.append('model_name', modelName);
          formData.append('flag', String(flag));
          formData.append('thread_id', threadId);
          formData.append('file', file);
          
          apiResponse = await this.client.postFormData<ChatResponse>('/api/chat', formData, signal);
        } else {
          console.warn('Файл без данных:', { name: firstFile.name, type: firstFile.type });
          
          const requestBody: Record<string, unknown> = {
            message: message.trim(),
            model_name: modelName,
            flag,
            thread_id: threadId
          };
          apiResponse = await this.client.postFormUrlEncoded<ChatResponse>('/api/chat', requestBody, signal);
        }
      } else {
        const requestBody: Record<string, unknown> = {
          message: message.trim(),
          model_name: modelName,
          flag,
          thread_id: threadId
        };
        apiResponse = await this.client.postFormUrlEncoded<ChatResponse>('/api/chat', requestBody, signal);
      }
      
      if (apiResponse && typeof apiResponse === 'object' && 'response' in apiResponse) {
        return {
          response: String(apiResponse.response),
          attachments: (apiResponse as ChatResponse).attachments
        };
      }
      
      const responseText = typeof apiResponse === 'string' ? apiResponse : JSON.stringify(apiResponse);
      return {
        response: responseText,
        attachments: undefined
      };
    } catch (error) {
      console.error('Chat API error:', error);
      throw error;
    }
  }

  async getChatHistory(threadId: string): Promise<Message[]> {
    if (!threadId?.trim()) {
      console.warn('Thread ID is empty, returning empty history');
      return [];
    }

    try {
      const response = await this.client.get<{
        chat_id: string, 
        messages: Array<{
          role: string, 
          content: string | {response?: string, attachments?: FileAttachment[]},
          timestamp?: string
        }>
      }>(`/api/history?chat_id=${threadId}`);
      
      if (response && response.messages && Array.isArray(response.messages)) {
        const messages: Message[] = response.messages.map((msg: {role: string, content: string | {response?: string, attachments?: FileAttachment[]}, timestamp?: string}, index: number) => {
          let contentString = '';
          let attachments: FileAttachment[] | undefined = undefined;

          let parsedContent: string | {response?: string, attachments?: FileAttachment[]} = msg.content;
          
          if (typeof msg.content === 'string') {
            try {
              const parsed = JSON.parse(msg.content) as {response?: string, attachments?: FileAttachment[]} | string;
              if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                parsedContent = parsed;
              } else {
                parsedContent = msg.content;
              }
            } catch {
              parsedContent = msg.content;
            }
          }

          if (typeof parsedContent === 'object' && parsedContent !== null && !Array.isArray(parsedContent)) {
            contentString = parsedContent.response || '';
            if (parsedContent.attachments && Array.isArray(parsedContent.attachments)) {
              attachments = parsedContent.attachments.map((att: FileAttachment) => ({
                ...att,
                id: String(att.id),
                uploadedAt: att.uploadedAt ? new Date(att.uploadedAt) : new Date(),
              }));
            }
          } else {
            contentString = typeof parsedContent === 'string' ? parsedContent : '';
          }

          return {
            id: `history-${threadId}-${index}`,
            type: (msg.role === 'user' ? 'human' : 'ai') as 'human' | 'ai',
            content: contentString,
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            createdAt: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            updatedAt: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            attachments,
          };
        });
        
        return messages;
      }
      
      return [];
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
      return [];
    }
  }
}
